import { useTranslation } from 'react-i18next'
import { useCallback, useMemo } from 'react'
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  InputGroup,
  NON_EU_COUNTRIES,
  NumericInputGroup,
  useAddressLatinCharacterValidation
} from '@/common'
import { InstallationAddress, InstallationLocalAddress } from '@/features/installations/api/InstallationModel'
import { useSession } from '@/shared/hooks/useSession'
import UnlocodeInput from '@/components/UnlocodeInput'
import ReactCountryFlag from 'react-country-flag'

function AddressForm({
  isLocal = true,
  street,
  street2,
  country,
  streetNumber,
  postCode,
  poBox,
  city,
  onInputChange
}: {
  isLocal?: boolean
  street?: string
  street2?: string
  country: string
  streetNumber?: string
  postCode?: string
  poBox?: string
  city?: string
  onInputChange: (evt: React.ChangeEvent<HTMLInputElement>) => void
}) {
  const { t } = useTranslation()
  const label = (key: string) => t(`installations:fields.${key}.label`)

  // Use generalized validation hook for English fields only
  const errors = useAddressLatinCharacterValidation(
    isLocal
      ? {}
      : {
          street: street || '',
          street2: street2 || '',
          streetNumber: streetNumber || '',
          postCode: postCode || '',
          poBox: poBox || '',
          city: city || ''
        },
    t('common:errors.latinCharacters')
  )

  return (
    <div>
      <p className="text-sm mb-1.5">
        {t('installations:create.sections.address.title')}{' '}
        <ReactCountryFlag countryCode={isLocal ? country : 'gb'} svg />
      </p>
      <div className="space-y-4 bg-background-main p-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <InputGroup
              id="street"
              label={label('street')}
              value={street}
              onChange={onInputChange}
              error={!isLocal ? errors.street || undefined : undefined}
            />
          </div>

          <InputGroup
            id="streetNumber"
            label={label('streetNumber')}
            value={streetNumber}
            onChange={onInputChange}
            error={!isLocal ? errors.streetNumber || undefined : undefined}
          />
        </div>

        <InputGroup
          id="street2"
          label={label('street2')}
          value={street2}
          onChange={onInputChange}
          tooltip={t('installations:fields.street2.tooltip')}
          tooltipPlacement="top"
          error={!isLocal ? errors.street2 || undefined : undefined}
        />

        <div className="grid md:grid-cols-3 gap-4">
          <InputGroup
            id="postCode"
            label={label('postCode')}
            value={postCode}
            onChange={onInputChange}
            tooltip={t('installations:fields.postCode.tooltip')}
            tooltipPlacement="top"
            required={
              NON_EU_COUNTRIES.find(nonEuCountry => nonEuCountry.value === country)
                ?.isPostCodeRequired
            }
            error={!isLocal ? errors.postCode || undefined : undefined}
          />
          <InputGroup
            id="city"
            label={label('city')}
            value={city}
            onChange={onInputChange}
            required
            error={!isLocal ? errors.city || undefined : undefined}
          />
          <InputGroup
            id="poBox"
            label={label('poBox')}
            value={poBox}
            onChange={onInputChange}
            error={!isLocal ? errors.poBox || undefined : undefined}
          />
        </div>
      </div>
    </div>
  )
}

export default function LocalAddressCard({
  id,
  onChange,
  state
}: {
  id: string
  onChange: (payload: InstallationAddress & InstallationLocalAddress) => void
  state: InstallationAddress & InstallationLocalAddress
}) {
  const {
    session: {
      organization: { placeOfRegistration: organizationPlaceOfRegistration }
    }
  } = useSession()

  const { t } = useTranslation()
  const {
    street,
    street2,
    country,
    streetNumber,
    postCode,
    poBox,
    city,
    unlocode,
    longitude,
    latitude,
    localCity,
    localStreet,
    localStreetNumber,
    localPostCode,
    localPoBox,
    localStreet2
  } = state

  /* ────────────────── Validation helpers ────────────────── */
  const longitudeError = useMemo(() => {
    if (longitude === null || longitude === undefined || String(longitude).trim() === '') {
      return undefined
    }
    const numericValue = Number(longitude)
    return numericValue < -180 || numericValue > 180
      ? t('installations:fields.longitude.errors.outOfRange', { min: -180, max: 180 })
      : undefined
  }, [longitude, t])

  const latitudeError = useMemo(() => {
    if (latitude === null || latitude === undefined || String(latitude).trim() === '') {
      return undefined
    }
    const numericValue = Number(latitude)
    return numericValue < -90 || numericValue > 90
      ? t('installations:fields.latitude.errors.outOfRange', { min: -90, max: 90 })
      : undefined
  }, [latitude, t])

  const label = (key: string) => t(`installations:fields.${key}.label`)
  const countryFullName = (country: string) => {
    return country ? t(`countriesByIsoAlpha2:${country}`) : ''
  }

  const onInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...state, [evt.target.id]: evt.target.value })
    },
    [state, onChange]
  )

  const onLocalInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const id = evt.target.id
      onChange({
        ...state,
        [`local${id.charAt(0).toUpperCase() + id.slice(1)}`]: evt.target.value
      })
    },
    [state, onChange]
  )

  const onUnLocodeChange = useCallback(
    (value: string) => {
      onChange({ ...state, unlocode: value })
    },
    [state, onChange]
  )

  return (
    <div id={id} className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle title={t('installations:create.sections.address.title')}></CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <Alert type="info">{t('installations:create.sections.address.localAddressInfo')}</Alert>
          <div className="grid grid-cols-2 gap-4">
            <AddressForm
              street={localStreet}
              street2={localStreet2}
              country={country}
              streetNumber={localStreetNumber}
              postCode={localPostCode}
              poBox={localPoBox}
              city={localCity}
              onInputChange={onLocalInputChange}
            />
            <AddressForm
              isLocal={false}
              street={street}
              street2={street2}
              country={country}
              streetNumber={streetNumber}
              postCode={postCode}
              poBox={poBox}
              city={city}
              onInputChange={onInputChange}
            />
          </div>
          <InputGroup
            id="country"
            label={label('country')}
            value={countryFullName(country)}
            disabled
          />
          <UnlocodeInput
            countryCodeFilter={organizationPlaceOfRegistration}
            label={label('unlocode')}
            value={unlocode || ''}
            tooltip={t('installations:fields.unlocode.tooltip')}
            tooltipPlacement="top"
            onChange={value => {
              onUnLocodeChange(value || '')
            }}
          />
          <div className="grid grid-cols-3 gap-4">
            <NumericInputGroup
              id="longitude"
              label={label('longitude')}
              value={longitude ? longitude.toString() : ''}
              onChange={onInputChange}
              tooltip={t('installations:fields.longitude.tooltip')}
              tooltipPlacement="top"
              error={longitudeError}
              numericFormat={{
                decimalScale: 6,
                min: -180,
                max: 180,
                fixedDecimalScale: true
              }}
            />
            <NumericInputGroup
              id="latitude"
              label={label('latitude')}
              value={latitude ? latitude.toString() : ''}
              onChange={onInputChange}
              tooltip={t('installations:fields.latitude.tooltip')}
              tooltipPlacement="top"
              error={latitudeError}
              numericFormat={{
                decimalScale: 6,
                min: -90,
                max: 90,
                fixedDecimalScale: true
              }}
            />
          </div>
        </CardBody>
      </Card>
    </div>
  )
}
